import { FormItem } from '@vindr/ui';
import { DeviceTypes, GENDER } from '@vindr/services/constants/common.constant';
import { MESSAGE } from '@vindr/services/constants/message.constant';
import { ORDER_STATUS } from '@vindr/services/constants/order.constant';
import { useGlobalContext } from '#contexts/GlobalContext';
import { useDebounce } from '@vindr/utils/hooks/useDebounce';
import { useGetDevices } from '@vindr/services/actions/devices.action';
import { useGetInstitutions } from '@vindr/services/actions/institution.action';
import { useUpdateOrder } from '@vindr/services/actions/order.action';
import { ParamsUpdateOrder } from '@vindr/services/apis/order.api';
import { uniquePushArray } from '@vindr/utils/helpers/common.helper';
import { convertPhoneNumber } from '@vindr/utils/formatText/formatText';
import { Button, Col, DatePicker, Form, Input, Row, Select, Typography } from 'antd';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ProcedureName from './ProcedureName';
import EditOrder from './index';
const { Title } = Typography;

const ORDER_STATUS_LIST = [
  { label: 'ARRIVED', value: ORDER_STATUS.ARRIVED },
  { label: 'READING', value: ORDER_STATUS.READING },
  { label: 'SUBMITTED', value: ORDER_STATUS.SUBMITTED },
  { label: 'APPROVED', value: ORDER_STATUS.APPROVED },
  { label: 'CANCEL', value: ORDER_STATUS.CANCEL },
  { label: 'COMPLETED', value: ORDER_STATUS.COMPLETED },
  { label: 'NEW', value: ORDER_STATUS.NEW },
];

const LIMIT_STATUS = [ORDER_STATUS.NEW, ORDER_STATUS.CANCEL, ORDER_STATUS.ARRIVED];

const EditOrderForm = ({
  orderData,
  onFinish,
}: {
  orderData: Parameters<typeof EditOrder>['0']['orderData'];
  onFinish: Parameters<typeof EditOrder>['0']['onFinish'];
}) => {
  const { t } = useTranslation('Vindoc');
  const { message } = useGlobalContext();

  const [orderForm] = Form.useForm();
  const [patientForm] = Form.useForm();
  const status = Form.useWatch('status', orderForm);
  const institution_id = Form.useWatch('institution_id', orderForm);

  const limitStatus = useMemo(() => {
    return LIMIT_STATUS.includes(status);
  }, [status]);

  const OrderStatusList = useMemo(() => {
    const translateStatusList = ORDER_STATUS_LIST.map((item) => ({
      ...item,
      label: t(item.label),
    }));

    if (limitStatus) {
      return LIMIT_STATUS.map((val) => translateStatusList.find((item) => item.value === val));
    }

    return translateStatusList;
  }, [limitStatus, t]);

  const { data: institutions } = useGetInstitutions({ offset: 0, limit: 100 })?.data || {};

  const [searchDevice, onSearchDevice] = useDebounce<string>();
  const { data: devices } =
    useGetDevices({
      offset: 0,
      limit: 100,
      institution_ids: [institution_id],
      active: true,
      types: [DeviceTypes.Modality],
      name: searchDevice,
    })?.data || {};

  const { mutate: updateOrder } = useUpdateOrder();

  useEffect(() => {
    if (!isEmpty(orderData)) {
      patientForm.setFieldsValue({
        ...orderData?.visited_patient,
        birth_date: orderData?.visited_patient?.birth_date
          ? dayjs(orderData?.visited_patient?.birth_date)
          : null,
        patient_name: orderData?.visited_patient?.name,
      });
      orderForm.setFieldsValue({
        ...orderData,
        scheduled_date: orderData?.scheduled_date && dayjs(orderData?.scheduled_date),
      });
    }
  }, [orderData]);

  const handleSave = () => {
    Promise.all([patientForm.validateFields(), orderForm.validateFields()])
      .then(([patient, order]) => {
        const input: ParamsUpdateOrder = {
          ...order,
          id: orderData?.id,
          visited_patient: {
            ...patient,
            phone: convertPhoneNumber(patient?.phone),
          },
        };

        updateOrder(input, {
          onSuccess: () => {
            message.success(t(MESSAGE.SUCCESS.SCD_8001));
            onFinish();
          },
          onError: (error: any) => {
            const error_message =
              (error?.response?.data?.error_code &&
                MESSAGE.ERROR[error?.response?.data?.error_code]) ||
              'Update error';

            message.error(t(error_message));
          },
        });
      })
      .catch((info) => {
        console.log('Validate Failed:', info);
      });
  };

  return (
    <div>
      <Row gutter={36}>
        <Col xs={24} xl={12}>
          <Title level={3} className="mb-6">
            {t('Patient Information')}
          </Title>
          <Form layout="vertical" form={orderForm} name="form" onFinish={handleSave}>
            <Form.Item
              name="institution_id"
              label={t('Institution')}
              className="mb-2"
              initialValue={institutions?.[0]?.id}
            >
              <Select placeholder={t('Select Institution')} disabled>
                {institutions
                  ? institutions.map((it, idx) => (
                      <Select.Option key={idx} value={it?.id}>
                        {it?.name}
                      </Select.Option>
                    ))
                  : null}
              </Select>
            </Form.Item>
          </Form>

          <Form
            layout="vertical"
            form={patientForm}
            name="form"
            onFinish={handleSave}
            className="form-filter-condition"
          >
            <Form.Item name="patient_name" label={t('Patient Name')} className="mb-2">
              <Input placeholder={t('Enter Patient Name')} />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} xl={12}>
                <FormItem.Birthday name="birth_date" className="mb-2" />
              </Col>
              <Col xs={24} xl={12}>
                <Form.Item
                  name="sex"
                  label={t('Gender')}
                  className="mb-2"
                  initialValue={GENDER.Other}
                >
                  <Select placeholder={t('Select')}>
                    <Select.Option value={GENDER.Male}>{t('Male')}</Select.Option>
                    <Select.Option value={GENDER.Female}>{t('Female')}</Select.Option>
                    <Select.Option value={GENDER.Other}>{t('Other')}</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} xl={12}>
                <Form.Item name="patient_id" label={t('Patient ID')} className="mb-2">
                  <Input placeholder={t('Enter Patient ID')} />
                </Form.Item>
              </Col>
              <Col xs={24} xl={12}>
                <Form.Item name="visit_id" label={t('Visit ID')} className="mb-2">
                  <Input placeholder={t('Enter Visit ID')} disabled />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} xl={12} className="w-full">
                <FormItem.PhoneNumber className="mb-2" />
              </Col>
              <Col xs={24} xl={12}>
                <FormItem.Email className="mb-2" />
              </Col>
            </Row>
            <FormItem.InputArea
              label={t('Address')}
              name="address"
              placeholder={t('Enter Address')}
              rows={1}
            />
          </Form>
        </Col>

        <Col xs={24} xl={12}>
          <Title level={3} className="mb-6">
            {t('Order Information')}
          </Title>
          <Form layout="vertical" form={orderForm} name="form" onFinish={handleSave}>
            <Form.Item
              name="scheduled_date"
              label={t('Scheduled Date')}
              className="mb-2"
              required
              rules={[{ required: true, message: t('Required value') }]}
              initialValue={dayjs()}
            >
              <DatePicker className="w-full" showTime format="MM/DD/YYYY HH:mm:ss" />
            </Form.Item>
            <Row gutter={16}>
              <Col xs={24} xl={12}>
                <FormItem.Select
                  label={t('Device')}
                  placeholder={t('Select Device')}
                  name="device_id"
                  wrapperClassName="mb-2"
                  className="mb-0"
                  required
                  searchVietnamese
                  showSearch
                  rules={[{ required: true, message: t('Required value') }]}
                  options={uniquePushArray(
                    devices?.map((it) => ({ label: it?.name, value: it?.id })),
                    {
                      label: orderData?.device?.name,
                      value: orderData?.device?.['device_id'],
                    }
                  )}
                  onSearch={onSearchDevice}
                  onClear={() => {
                    onSearchDevice(null);
                  }}
                  allowClear={false}
                />
              </Col>
              <Col xs={24} xl={12}>
                <FormItem.Input
                  name="order_group"
                  label={t('Order Group')}
                  placeholder={t('Enter Order Group')}
                  wrapperClassName="mb-2"
                  className="mb-2"
                  disabled
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} xl={12}>
                <FormItem.Input
                  name="refer_doctor_name"
                  label={t('Refer Doctor')}
                  placeholder={t('Enter Refer Doctor')}
                  className="mb-2"
                />
              </Col>
              <Col xs={24} xl={12}>
                <FormItem.Input
                  name="order_doctor_name"
                  label={t('Order Doctor')}
                  placeholder={t('Enter Order Doctor')}
                  className="mb-2"
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <FormItem.Input
                  name="diagnostic"
                  label={t('Diagnoses')}
                  placeholder={t('Enter Diagnoses')}
                  className="mb-2"
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <ProcedureName orderData={orderData} />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <FormItem.Select
                  name="status"
                  label={t('Status')}
                  placeholder={t('Select')}
                  className="mb-2"
                  disabled={!limitStatus}
                  options={OrderStatusList}
                />
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
      <div className="footer mt-3 flex justify-end">
        <Button onClick={onFinish} className="mr-1">
          {t('Cancel')}
        </Button>
        <Button type="primary" onClick={handleSave}>
          {t('Save')}
        </Button>
      </div>
    </div>
  );
};

export default EditOrderForm;
